import { useCallback, useRef, useState } from 'react';
import type { NavItem } from './nav-tree.tsx';

/**
 * Tree-keyboard navigation per WAI-ARIA tree pattern. Owns "which row
 * has tab focus" + arrow-key handler. Decoupled from Sidebar so it can
 * be unit-tested without React rendering (logic exposed as pure fns).
 *
 * Layout:
 *   - Sidebar walks visible tree depth-first into a flat focusable
 *     list. Closed groups contribute only the group row; open groups
 *     contribute the row + their children.
 *   - The hook tracks `focusedKey` (string) — `${groupKey}` for a
 *     group row, `${leafTo}` for a leaf.
 *   - Tab still enters via the active leaf (Mantine NavLink default);
 *     arrows then take over.
 *
 * Key bindings:
 *   ↓ — next focusable
 *   ↑ — previous focusable
 *   → on collapsed group — open it; on open group — focus first child
 *   ← on group child — focus parent group; on open group — close
 *   Home / End — jump to first / last
 */

export interface FocusableRow {
  readonly key: string;
  readonly type: 'leaf' | 'group';
  readonly groupKey?: string; // parent group key when type=leaf inside group
}

/** Walk the visible tree DFS into a flat focusable list. */
export function buildFocusableList(
  tree: readonly NavItem[],
  isOpen: (key: string) => boolean,
): FocusableRow[] {
  const out: FocusableRow[] = [];
  for (const n of tree) {
    if (n.type === 'leaf') {
      out.push({ key: n.to, type: 'leaf' });
    } else {
      out.push({ key: n.key, type: 'group' });
      if (isOpen(n.key)) {
        for (const c of n.children) {
          out.push({ key: c.to, type: 'leaf', groupKey: n.key });
        }
      }
    }
  }
  return out;
}

/** Find the index of a key, or -1. */
export function indexOfKey(rows: readonly FocusableRow[], key: string | null): number {
  if (!key) return -1;
  return rows.findIndex((r) => r.key === key);
}

export interface UseSidebarKeyboardNav {
  readonly focusedKey: string | null;
  readonly setFocusedKey: (key: string | null) => void;
  readonly handleKeyDown: (e: React.KeyboardEvent, currentKey: string) => void;
  /** Attach to the focusable element to register its DOM node so
   *  scrollIntoView + focus() can target it on programmatic move. */
  readonly registerNode: (key: string) => (node: HTMLElement | null) => void;
}

export function useSidebarKeyboardNav(
  tree: readonly NavItem[],
  isOpen: (key: string) => boolean,
  toggle: (key: string) => void,
): UseSidebarKeyboardNav {
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const nodeRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  const registerNode = useCallback(
    (key: string) => (node: HTMLElement | null) => {
      if (node === null) {
        nodeRefs.current.delete(key);
      } else {
        nodeRefs.current.set(key, node);
      }
    },
    [],
  );

  const focusKey = useCallback((key: string) => {
    setFocusedKey(key);
    const node = nodeRefs.current.get(key);
    if (node) {
      // Defer to next tick so React commits tabIndex change first.
      queueMicrotask(() => {
        node.focus();
        node.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
    }
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, currentKey: string) => {
      const rows = buildFocusableList(tree, isOpen);
      const idx = indexOfKey(rows, currentKey);
      if (idx === -1) return;

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          const next = rows[(idx + 1) % rows.length]!;
          focusKey(next.key);
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          const prev = rows[(idx - 1 + rows.length) % rows.length]!;
          focusKey(prev.key);
          break;
        }
        case 'ArrowRight': {
          const cur = rows[idx]!;
          if (cur.type === 'group') {
            e.preventDefault();
            if (!isOpen(cur.key)) {
              toggle(cur.key);
            } else {
              // already open → focus first child if any
              const next = rows[idx + 1];
              if (next && next.groupKey === cur.key) focusKey(next.key);
            }
          }
          break;
        }
        case 'ArrowLeft': {
          const cur = rows[idx]!;
          if (cur.type === 'leaf' && cur.groupKey) {
            e.preventDefault();
            focusKey(cur.groupKey);
          } else if (cur.type === 'group' && isOpen(cur.key)) {
            e.preventDefault();
            toggle(cur.key);
          }
          break;
        }
        case 'Home': {
          e.preventDefault();
          if (rows[0]) focusKey(rows[0].key);
          break;
        }
        case 'End': {
          e.preventDefault();
          const last = rows[rows.length - 1];
          if (last) focusKey(last.key);
          break;
        }
      }
    },
    [tree, isOpen, toggle, focusKey],
  );

  return { focusedKey, setFocusedKey, handleKeyDown, registerNode };
}
